import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Tag, Send, Sparkles, Building2, Check, Clock, ArrowRight, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const money = (q, v) => (v === 0 ? null : `${q?.currency === "USD" ? "$" : ""}${v}`);

export default function VipAccess() {
  const { t } = useLang();
  const navigate = useNavigate();
  useSEO("VIP Access");
  const [status, setStatus] = useState(null);
  const [quote, setQuote] = useState(null);
  const [promo, setPromo] = useState("");
  const [loading, setLoading] = useState(false);

  const loadQuote = (code) =>
    api.get("/vip/quote" + (code ? `?_=${Date.now()}` : "")).then((r) => setQuote(r.data)).catch(() => {});

  const load = () => {
    api.get("/vip/status").then((r) => {
      setStatus(r.data);
      const pc = r.data?.latest_request?.promo_code;
      if (pc && !promo) setPromo(pc);
    }).catch(() => {});
    loadQuote();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const { data } = await api.post("/vip/apply-promo", { code: promo.trim() });
      setQuote(data);
      if (data.promo_blocked) toast.warning(t("vip_promo_not_combinable"));
      else toast.success("Promo applied");
    } catch (err) {
      // still refresh quote to show reason
      await loadQuote();
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const requestVip = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/vip/request", { promo_code: promo.trim() || null });
      toast.success(data.updated ? "Your VIP request was updated" : "VIP request submitted for review");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const vip = status?.vip;
  const req = status?.latest_request;
  const pending = req?.status === "pending";
  const q = quote || {};
  const curr = q.currency === "USD" ? "$" : "";
  const priceStr = q.final_price === 0 ? t("vip_free") : `${curr}${q.final_price}`;
  const brokerLine = (q.applied_benefits || []).find((b) => b.source === "broker");
  const promoLine = (q.applied_benefits || []).find((b) => b.source === "promo");

  const rowText = (b) => {
    if (b.type === "discount") return `-${b.value}%`;
    if (b.type === "free_first_month") return t("vip_free");
    if (b.type === "free_duration") return `${b.value}d ${t("vip_free")}`;
    return t("vip_free");
  };

  return (
    <div className="space-y-6" data-testid="vip-access-page">
      <h1 className="font-display text-2xl font-bold text-white">{t("vip_status")}</h1>

      {/* VIP STATUS */}
      <div className="card-surface rounded-2xl p-7 relative overflow-hidden">
        <div className="absolute inset-0 gradient-brand opacity-[0.06]" />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl gradient-brand flex items-center justify-center"><Crown className="h-7 w-7 text-brand-navy" /></div>
            <div>
              <p className="font-display text-xl font-bold text-white" data-testid="vip-status-label">{vip?.active ? t("vip_active_label") : t("vip_not_member")}</p>
              <p className="text-sm text-slate-400">
                {vip?.active
                  ? (vip.lifetime ? t("vip_lifetime_access") : vip.expires_at ? `${t("vip_expires")} ${new Date(vip.expires_at).toLocaleDateString()}` : t("vip_active_label"))
                  : `INVIXA VIP · ${curr}${q.base_price ?? ""} ${t("vip_per_month")}`}
              </p>
              {vip?.active && req?.quote?.applied_benefits?.length > 0 && (
                <p className="mt-1 text-xs text-brand-green flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> {req.quote.applied_benefits.map((b) => b.label).join(" · ")}</p>
              )}
            </div>
          </div>
          {vip?.active && status?.telegram_vip_link && (
            <a href={status.telegram_vip_link} target="_blank" rel="noopener noreferrer"><Button className="gradient-brand text-brand-navy font-semibold rounded-full" data-testid="vip-telegram-btn"><Send className="h-4 w-4 mr-2" /> {t("vip_join_telegram")}</Button></a>
          )}
        </div>
      </div>

      {!vip?.active && (
        <>
          {pending && (
            <div className="card-surface rounded-2xl p-4 border-amber-500/20 bg-amber-500/5 text-sm text-amber-300 flex items-center gap-2" data-testid="vip-pending">
              <Clock className="h-4 w-4 shrink-0" /> {t("vip_pending_note")}
            </div>
          )}

          {/* VIP MEMBERSHIP + BASE PRICE + PROMO */}
          <div className="card-surface rounded-2xl p-7">
            <div className="flex items-center gap-2 mb-1"><Crown className="h-5 w-5 text-brand-cyan" /><h2 className="font-display font-semibold text-white">INVIXA VIP</h2></div>
            <p className="text-sm text-slate-400">{t("vip_base_price")}</p>
            <div className="flex items-end gap-2 mt-1 mb-5">
              <span className="font-display text-3xl font-extrabold text-white" data-testid="vip-base-price">{curr}{q.base_price ?? "—"}</span>
              <span className="text-slate-500 mb-1">{t("vip_per_month")}</span>
            </div>
            <label className="text-sm text-slate-300">{t("vip_promo_label")}</label>
            <div className="flex gap-2 max-w-md mt-1.5">
              <Input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder={t("vip_promo_placeholder")} data-testid="vip-promo-input" className="bg-white/5 border-white/10" />
              <Button onClick={applyPromo} variant="outline" className="border-white/15 text-white" data-testid="vip-apply-promo"><Tag className="h-4 w-4 mr-1.5" /> {t("vip_apply")}</Button>
            </div>
            {q.promo_blocked && <p className="mt-2 text-xs text-amber-400 flex items-center gap-1.5" data-testid="vip-promo-blocked"><AlertTriangle className="h-3.5 w-3.5" /> {t("vip_promo_not_combinable")}</p>}
            {q.promo_applied && promoLine && <p className="mt-2 text-xs text-brand-green flex items-center gap-1.5" data-testid="vip-promo-applied"><Check className="h-3.5 w-3.5" /> {promoLine.label}</p>}
            {q.promo_message && !q.promo_blocked && !q.promo_applied && <p className="mt-2 text-xs text-slate-500">{q.promo_message}</p>}
          </div>

          {/* BROKER BENEFIT BANNER */}
          <BrokerBanner q={q} t={t} navigate={navigate} />

          {/* LIVE VIP QUOTE */}
          <div className="card-surface rounded-2xl p-7" data-testid="vip-quote-summary">
            <div className="flex items-center gap-2 mb-4"><Sparkles className="h-5 w-5 text-brand-cyan" /><h2 className="font-display font-semibold text-white">{t("vip_quote_title")}</h2></div>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between text-slate-300"><span>{t("vip_base_row")}</span><span>{curr}{q.base_price ?? "—"}{t("vip_per_month")}</span></div>
              {brokerLine && <div className="flex justify-between text-brand-green" data-testid="quote-broker-row"><span>{t("vip_broker_benefit_row")}</span><span>{rowText(brokerLine)}</span></div>}
              {promoLine && <div className="flex justify-between text-brand-green" data-testid="quote-promo-row"><span>{t("vip_promo_row")}</span><span>{rowText(promoLine)}</span></div>}
              <div className="border-t border-white/10 pt-3 flex justify-between items-end">
                <span className="font-display font-semibold text-white">{t("vip_your_price")}</span>
                <span className="font-display text-2xl font-extrabold text-white" data-testid="vip-final-price">{priceStr}{q.final_price !== 0 && <span className="text-sm text-slate-500 font-normal">{t("vip_per_month")}</span>}</span>
              </div>
              {q.free_until && q.final_price !== 0 && (
                <p className="text-xs text-brand-cyan text-right" data-testid="vip-free-until">{t("vip_free_until")} {new Date(q.free_until).toLocaleDateString()} · {t("vip_first_then")} {curr}{q.base_price}{t("vip_per_month")}</p>
              )}
              {q.free_until && q.final_price === 0 && (
                <p className="text-xs text-brand-cyan text-right" data-testid="vip-free-until">{t("vip_free_until")} {new Date(q.free_until).toLocaleDateString()}</p>
              )}
            </div>
          </div>

          {/* SINGLE CTA */}
          <Button onClick={requestVip} disabled={loading} className="w-full gradient-brand text-brand-navy font-semibold rounded-full py-6 text-base" data-testid="vip-request-btn">
            <Crown className="h-5 w-5 mr-2" /> {pending ? t("vip_update_request") : t("vip_request_join")}
          </Button>
          <p className="text-xs text-slate-600 text-center">{t("vip_payment_note")}</p>
        </>
      )}
    </div>
  );
}

function BrokerBanner({ q, t, navigate }) {
  if (q.broker_status === "approved" && q.broker_benefit) {
    return (
      <div className="rounded-2xl p-[1px] gradient-brand" data-testid="broker-banner-unlocked">
        <div className="rounded-2xl bg-brand-navy2 p-6 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-brand-green/15 flex items-center justify-center"><Check className="h-6 w-6 text-brand-green" /></div>
          <div className="flex-1">
            <p className="font-display font-semibold text-white flex items-center gap-2">{t("vip_broker_unlocked")} <Check className="h-4 w-4 text-brand-green" /></p>
            <p className="text-sm text-slate-400">{q.broker_benefit.broker_name}</p>
          </div>
          <span className="font-display text-lg font-extrabold text-gradient-brand">{q.broker_benefit.display}</span>
        </div>
      </div>
    );
  }
  if (q.broker_status === "pending") {
    return (
      <div className="card-surface rounded-2xl p-6 border-amber-500/20 flex items-center gap-4" data-testid="broker-banner-pending">
        <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center"><Clock className="h-6 w-6 text-amber-400" /></div>
        <div>
          <p className="font-display font-semibold text-white">{t("vip_broker_pending")}</p>
          <p className="text-sm text-slate-400">{t("vip_broker_pending_desc")}</p>
        </div>
      </div>
    );
  }
  // generic open-account banner
  const disp = q.featured_broker?.display;
  return (
    <div className="relative overflow-hidden rounded-2xl card-surface p-6 sm:p-7 border-brand-cyan/20" data-testid="broker-banner-open">
      <div className="absolute inset-0 gradient-brand opacity-[0.07]" />
      <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-5">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl gradient-brand flex items-center justify-center shrink-0"><Building2 className="h-6 w-6 text-brand-navy" /></div>
          <div>
            <p className="font-display text-lg sm:text-xl font-extrabold text-white">
              {t("vip_open_account_get")} {disp ? <span className="text-gradient-brand">{disp}</span> : <span className="text-gradient-brand">VIP BENEFIT</span>}
            </p>
            <p className="mt-1 text-sm text-slate-400 max-w-md">{t("vip_broker_banner_sub")}</p>
          </div>
        </div>
        <Button onClick={() => navigate("/brokers")} className="gradient-brand text-brand-navy font-semibold rounded-full shrink-0" data-testid="broker-banner-cta">{t("vip_view_brokers")} <ArrowRight className="h-4 w-4 ml-1.5 rtl-mirror" /></Button>
      </div>
    </div>
  );
}
